import isEqual from 'lodash.isequal';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { CHART_DATA_KEYS, CHART_TYPES } from '_constants/charts';
import { FIELD_KEYS } from '_constants/forms';
import { REPORT_KEYS } from '_constants/reports';
import { theme } from '_constants/theme';
import { TRANSACTION_TYPES } from '_constants/transactions';
import { createChartData, createChartProps } from '_helpers/charts';
import { usePrevious } from '_hooks';
import { selectActiveMainProductId } from '_store/order/selector';
import { selectData, selectFilters, selectParams } from '_store/reports/selector';
import { getInsightsGraph } from '_store/reports/slice';
import { selectCustomerId } from '_store/user/selector';

import Chart from 'components/atomic/Chart';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

import Filters from './Filters';

const CHART_DATA_KEYS_MAP = {
  [TRANSACTION_TYPES.spend]: CHART_DATA_KEYS.expenses,
  [TRANSACTION_TYPES.receive]: CHART_DATA_KEYS.income,
};

const StyledChartContainer = styled.div`
  height: 300px;

  @media ${theme.devices.tablet} {
  }
`;

const InsightsView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);
  const prevProductId = usePrevious(productId);
  const insightsReportParams = useSelector(selectParams(REPORT_KEYS.insightsGraph));
  const prevInsightsReportParams = usePrevious(insightsReportParams);
  const data = useSelector(selectData(REPORT_KEYS.insightsGraph));
  const filters = useSelector(selectFilters(REPORT_KEYS.insightsGraph));

  useEffect(() => {
    if (customerId && productId && !isEqual(prevInsightsReportParams, insightsReportParams)) {
      dispatch(getInsightsGraph({ customerId, productId }));
    }
  }, [customerId, productId, prevInsightsReportParams, insightsReportParams]);

  useEffect(() => {
    if (customerId && productId && insightsReportParams && prevProductId !== productId) {
      dispatch(getInsightsGraph({ customerId, productId }));
    }
  }, [customerId, prevProductId, productId, insightsReportParams]);

  const chartProps = useMemo(
    () =>
      createChartProps({
        type: CHART_TYPES.line,
        [FIELD_KEYS.filterType]: filters[FIELD_KEYS.filterType],
        data: createChartData({
          [FIELD_KEYS.type]: CHART_TYPES.line,
          [FIELD_KEYS.filterType]: filters[FIELD_KEYS.filterType],
          data: {
            [CHART_DATA_KEYS_MAP[insightsReportParams?.type] || 'data']: data?.expensesGraph || {},
          },
        }),
      }),
    [data?.expensesGraph]
  );

  return (
    <Card fullWidth>
      <Text size={theme.fontSizes.medium} weight={600} bottomSpace={theme.spaces.medium}>
        Insights
      </Text>
      <Filters />
      <StyledChartContainer>
        <Chart type={CHART_TYPES.line} {...chartProps} />
      </StyledChartContainer>
    </Card>
  );
};

export default InsightsView;
